import { Component, OnInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { of, ReplaySubject } from 'rxjs';
import { takeUntil, concatMap, toArray, tap, delay } from 'rxjs/operators';
import { WS_SERVICE_FACTORY } from 'types';
import { CameraPhotoMode, CameraServiceBase } from '../../service/camera.service';
import { PortraitServiceBase } from '../../service/portrait.service';
import { WizardContext } from '../../base/control/context';
import { Control } from '../../base/control/control';
import { ResizedEvent } from '../../base/directive/element-resize.directive';
import {
  PortraitServiceState,
  CameraServiceState,
  SizeInfo,
  CameraInfo,
  Position,
  EyePositionInfo,
  PhotoProcessingResult,
  CameraType,
  HW_SERVICE_MOCK,
} from '../../service/hw-service-model';
import { CameraSettings, ComponentType } from '../cam.auto.connect';
import { AutoAction, BatteryAutoActionHelper } from '../auto.action';
import { HWServiceConfig } from '../../service/hw-service-config';
import { SystemService, SystemServiceFactory } from '../../service/system.service';
import { cameraServiceFactory, portraitServiceFactory } from '../../service/hw-service-factory';

@Component({
  selector: 'i-photo-wizard',
  templateUrl: './photo-wizard.component.html',
  styleUrls: ['./photo-wizard.component.scss'],
  providers: [
    { provide: PortraitServiceBase, useFactory: portraitServiceFactory, deps: [HW_SERVICE_MOCK, HWServiceConfig, WS_SERVICE_FACTORY] },
    {
      provide: CameraServiceBase,
      useFactory: cameraServiceFactory,
      deps: [HW_SERVICE_MOCK, HWServiceConfig, WS_SERVICE_FACTORY, MatDialog],
    },
  ],
})
export class PhotoWizardComponent implements OnInit, OnDestroy {
  private readonly PHOTO_ARRAY_KEY = 'B73A4A37-5BDE-4E10-811F-3838216F80C7';
  private readonly PHOTO_INDEX_KEY = 'DC164008-4518-4311-AC23-ED032B09DDC3';
  private readonly PHOTO_PROCESSING_DELAY = 500;

  private imageSrc: SafeUrl | null = null;
  private imageFitMode: string | null = null;
  private imageSize: SizeInfo | null = null;
  private frameSize: SizeInfo | null = null;
  private photoSize: SizeInfo | null = null;
  private containerSize: SizeInfo | null = null;
  private chipPhoto: string | null = null;
  private chipPhotoSrc: SafeUrl | null = null;
  private portraitPhoto: string | null = null;
  private portraitPhotoSrc: SafeUrl | null = null;
  private marginLeft: number = 0;
  private marginTop: number = 0;
  private imageResizeRatio: number = 1;
  private originalLeftEyePosition: Position | null = null;
  private originalRightEyePosition: Position | null = null;
  private currentLeftEyePosition: Position | null = null;
  private currentRightEyePosition: Position | null = null;

  photoArray: PhotoWizardResult[] = [];
  photoIndex: number = 0;
  camList: CameraInfo[];
  rotateToogle: boolean = true;
  ligthsOn: boolean = false;
  expasoftOn: boolean = false;
  mode: CameraPhotoMode = 'simple';
  TIME: string;

  private dead: ReplaySubject<void> = new ReplaySubject();
  private autoCancel = new AutoAction(
    (text) => (this.TIME = text),
    () => this.cancel(),
  );
  private systemService: SystemService;
  private batteryHelper: BatteryAutoActionHelper;
  photoWizardState: PhotoWizardState = PhotoWizardState.Closed;

  @Output() onComponentClosureRequested = new EventEmitter<PhotoWizardResult | null>();

  public constructor(
    private cameraService: CameraServiceBase,
    private portraitService: PortraitServiceBase,
    private sanitizer: DomSanitizer,
    private wizardContext: WizardContext,
    private settingsServiceFactory: SystemServiceFactory,
    private config: HWServiceConfig,
  ) {
    this.systemService = this.settingsServiceFactory.create(this.config);
    this.batteryHelper = new BatteryAutoActionHelper(this.autoCancel, this.systemService, this.dead);
  }

  public onImageContainerResized(event: ResizedEvent): void {
    this.containerSize = new SizeInfo(event.width, event.height, null);
    this.updateImageFitParams();
  }

  public ngOnInit(): void {
    const photoArray = this.wizardContext.getUserData<PhotoWizardResult[]>(this.PHOTO_ARRAY_KEY);
    if (photoArray !== undefined && photoArray instanceof Array) {
      this.photoArray = photoArray;
    }
    const photoIndex = this.wizardContext.getUserData<number>(this.PHOTO_INDEX_KEY);
    if (photoIndex !== undefined && typeof photoIndex === 'number') {
      this.photoIndex = photoIndex;
    }
    this.cameraService.cameraState$.pipe(takeUntil(this.dead)).subscribe(
      (state) => {
        this.onCameraStateChanged(state);
      },
      (error) => {
        this.handleError(error, PhotoWizardState.InitializationError);
      },
    );
    this.photoWizardState = PhotoWizardState.InitializationInProgress;
    setTimeout(() => {
      this.cameraService.connect();
    }, 1);
    this.batteryHelper.run();
  }

  public ngOnDestroy(): void {
    this.wizardContext.setUserData(this.PHOTO_ARRAY_KEY, this.photoArray);
    this.wizardContext.setUserData(this.PHOTO_INDEX_KEY, this.photoIndex);
    this.resetParameters();
    this.shutdownServices();
    this.dead.next();
    this.dead.complete();
  }

  public get state$(): string {
    return PhotoWizardState[this.photoWizardState];
  }

  public get isInitializationScreenVisible$(): boolean {
    return this.photoWizardState === PhotoWizardState.InitializationInProgress;
  }

  public get isCropScreenVisible$(): boolean {
    return this.photoWizardState === PhotoWizardState.PhotoProcessingInProgress;
  }

  public get isInitializationErrorScreenVisible$(): boolean {
    return this.photoWizardState === PhotoWizardState.InitializationError;
  }

  public get rotateCameraScreen$(): boolean {
    return this.photoWizardState === PhotoWizardState.RotateCameraScreen;
  }

  public get localPreview$(): boolean {
    return this.photoWizardState === PhotoWizardState.Preview;
  }

  public get shootingInProgress$(): boolean {
    return this.photoWizardState === PhotoWizardState.ShootingInProgress;
  }

  public get isShootingScreenVisible$(): boolean {
    return (
      this.photoWizardState !== PhotoWizardState.InitializationInProgress &&
      this.photoWizardState !== PhotoWizardState.InitializationError &&
      this.photoWizardState !== PhotoWizardState.ShootingResultConfirmation &&
      this.photoWizardState !== PhotoWizardState.ShutdownInProgress &&
      this.photoWizardState !== PhotoWizardState.Closed
    );
  }

  public get isConfirmationScreenVisible$(): boolean {
    return this.photoWizardState === PhotoWizardState.ShootingResultConfirmation;
  }

  public get chipPhotoSrc$(): SafeUrl | null {
    return this.chipPhotoSrc;
  }

  public get portraitPhotoSrc$(): SafeUrl | null {
    return this.portraitPhotoSrc;
  }

  public get imageSource$(): SafeUrl | null {
    return this.imageSrc;
  }

  public get imageFitMode$(): string | null {
    return this.imageFitMode;
  }

  public get marginLeft$(): number {
    return this.marginLeft;
  }

  public get marginTop$(): number {
    return this.marginTop;
  }

  public get originalLeftEyeMarkerPosition$(): Position {
    return this.originalLeftEyePosition ? this.calculateElementPosition(this.originalLeftEyePosition) : new Position(0, 0);
  }

  public get originalRightEyeMarkerPosition$(): Position {
    return this.originalRightEyePosition ? this.calculateElementPosition(this.originalRightEyePosition) : new Position(0, 0);
  }

  public get currentLeftEyeMarkerPosition$(): Position {
    return this.currentLeftEyePosition ? this.calculateElementPosition(this.currentLeftEyePosition) : new Position(0, 0);
  }

  public get currentRightEyeMarkerPosition$(): Position {
    return this.currentRightEyePosition ? this.calculateElementPosition(this.currentRightEyePosition) : new Position(0, 0);
  }

  public switchMode(mode: 'simple' | 'smart' | 'hdr') {
    this.mode = mode;
    this.photoWizardState = PhotoWizardState.RotateCameraScreen;
    this.cameraService
      .stopPreview()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          let selectedCamera: CameraInfo = this.camList.find((ci) => ci.id === this.cameraService.cameraId$)!;
          this.startPreview(selectedCamera);
        },
        (error) => this.handleError(error, PhotoWizardState.InitializationError),
      );
  }

  public onLeftEyeDrop(e: CdkDragEnd) {
    if (this.currentLeftEyePosition) {
      this.currentLeftEyePosition.x += Math.round(e.distance.x / this.imageResizeRatio);
      this.currentLeftEyePosition.y += Math.round(e.distance.y / this.imageResizeRatio);
    }
  }

  public onRightEyeDrop(e: CdkDragEnd) {
    if (this.currentRightEyePosition) {
      this.currentRightEyePosition.x += Math.round(e.distance.x / this.imageResizeRatio);
      this.currentRightEyePosition.y += Math.round(e.distance.y / this.imageResizeRatio);
    }
  }

  public enroll(): void {
    this.cameraService
      .stopPreview()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => this.startPhotoRender(),
        (e) => console.error('cannot stop preview'),
      );
  }

  private startPhotoRender() {
    let portraitServiceID: string;
    this.photoWizardState = PhotoWizardState.ShootingInProgress;
    this.portraitService
      .getID()
      .pipe(
        tap((id) => (portraitServiceID = id)),
        concatMap((_) =>
          this.cameraService.startStreamingForTakePhoto(this.photoSize!, this.mode, portraitServiceID).pipe(
            takeUntil(this.cameraService.endOfStream$),
            tap(() => {
              if (this.ligthsOn) {
                this.setLightMode(false);
              } else {
                of(this.setLightMode(true))
                  .pipe(delay(this.PHOTO_PROCESSING_DELAY))
                  .subscribe(() => this.setLightMode(false));
              }
            }),
            delay(this.PHOTO_PROCESSING_DELAY),
          ),
        ),
        toArray(),
        delay(this.mode === 'hdr' ? 0 : 150), // several first frames may have low quality when AUTO settings are used
        concatMap((_) => (this.mode === 'hdr' ? of(null) : this.cameraService.enroll(portraitServiceID, false, 'raw'))),
        takeUntil(this.dead),
      )
      .subscribe(
        (r) => {},
        (error) => {
          this.setLightMode(false);
          this.handleError(error);
        },
        () => {
          this.setLightMode(false);
          this.onEnrollComplete();
        },
      );
  }

  public process(): void {
    this.photoWizardState = PhotoWizardState.PhotoProcessingInProgress;
    let eyes = new EyePositionInfo();
    if (this.currentLeftEyePosition!.x > this.currentRightEyePosition!.x) {
      eyes.left = this.currentRightEyePosition!.copy();
      eyes.right = this.currentLeftEyePosition!.copy();
    } else {
      eyes.left = this.currentLeftEyePosition!.copy();
      eyes.right = this.currentRightEyePosition!.copy();
    }
    this.portraitService
      .setEyes(eyes)
      .pipe(takeUntil(this.dead))
      .subscribe(
        (result) => {
          this.onEyesPositionRegistered();
        },
        (error) => {
          this.handleError(error);
        },
      );
  }

  public apply(): void {
    if (this.photoArray.length == 1) {
      this.onComponentClosureRequested.emit(this.photoArray[0]);
    } else {
      this.onComponentClosureRequested.emit(this.photoArray[this.photoIndex]);
    }
  }
  public restart(): void {
    this.rotateToogle = true;
    this.resetParameters();
    this.photoWizardState = PhotoWizardState.InitializationInProgress;
    this.portraitService
      .clear()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (result) => {
          this.onPortraitServiceReset();
        },
        (error) => {
          this.onPortraitServiceReset();
        },
      );
  }
  public cancel(): void {
    this.onComponentClosureRequested.emit(null);
  }

  public canChangeLightMode(): boolean {
    let selectedCamera: CameraInfo = this.camList.find((ci) => ci.id === this.cameraService.cameraId$)!;
    return this.localPreview$ && selectedCamera.type === CameraType.Back;
  }

  public changeLightMode() {
    let enable = !this.ligthsOn;
    this.setLightMode(enable);
    CameraSettings.setLightMode(ComponentType.PhotoWizard, enable);
  }

  private setLightMode(enable: boolean) {
    this.ligthsOn = enable;
    this.cameraService.lights(enable);
  }

  public changeExpasoftMode() {
    this.expasoftOn = !this.expasoftOn;
  }

  public selectPhoto(index: number) {
    this.photoIndex = index;
  }

  private onPortraitServiceReset(): void {
    this.cameraService
      .getCameraList()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (list) => {
          this.onCameraListReceived(list);
        },
        (error) => {
          this.handleError(error, PhotoWizardState.InitializationError);
        },
      );
  }

  private onCameraStateChanged(state: CameraServiceState): void {
    if (state !== CameraServiceState.Closed && this.photoWizardState === PhotoWizardState.ShutdownInProgress) {
      this.shutdownServices();
      return;
    }

    if (state === CameraServiceState.Ready && this.photoWizardState === PhotoWizardState.InitializationInProgress) {
      this.portraitService.connectionState$.pipe(takeUntil(this.dead)).subscribe(
        (state) => {
          this.onProcessorStateShanged(state);
        },
        (error) => {
          this.handleError(error, PhotoWizardState.InitializationError);
        },
      );
      this.portraitService.connect();
    }
  }

  private onProcessorStateShanged(state: PortraitServiceState): void {
    if (state !== PortraitServiceState.Closed && this.photoWizardState === PhotoWizardState.ShutdownInProgress) {
      this.shutdownServices();
      return;
    }
    if (state === PortraitServiceState.Ready && this.photoWizardState === PhotoWizardState.InitializationInProgress) {
      if (this.photoArray.length) {
        this.photoWizardState = PhotoWizardState.ShootingResultConfirmation;
      } else {
        this.cameraService
          .getCameraList()
          .pipe(takeUntil(this.dead))
          .subscribe(
            (list) => {
              this.onCameraListReceived(list);
            },
            (error) => {
              this.handleError(error, PhotoWizardState.InitializationError);
            },
          );
      }
    }
  }

  private onCameraListReceived(list: CameraInfo[]): void {
    this.camList = list;
    let selectedCamera = CameraSettings.selectCamera(ComponentType.PhotoWizard, list, CameraType.Back);
    if (selectedCamera) {
      this.startPreview(selectedCamera);
    } else {
      this.handleError('Невозможно подключиться к камере', PhotoWizardState.InitializationError);
    }
  }

  rotate() {
    this.photoWizardState = PhotoWizardState.RotateCameraScreen;
    this.cameraService
      .stopPreview()
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          let anotherCamera = CameraSettings.selectAnotherCamera(ComponentType.PhotoWizard, this.camList, this.cameraService.cameraId$);
          if (anotherCamera) {
            this.rotateToogle = !this.rotateToogle;
            this.startPreview(anotherCamera);
          } else {
            this.handleError('Невозможно подключиться к камере', PhotoWizardState.InitializationError);
          }
        },
        (error) => this.handleError(error, PhotoWizardState.InitializationError),
      );
  }

  private startPreview(selectedCamera: CameraInfo) {
    this.cameraService.cameraId$ = selectedCamera.id;
    this.frameSize = CameraSettings.selectFrameForPortrait(selectedCamera.frames, 4, 3, 160, 160, false);
    this.photoSize = CameraSettings.selectFrameForPortrait(selectedCamera.frames, 4, 3, this.mode === 'hdr' ? 400 : 800, 400, true);
    this.cameraService.startLocalPreviewForPhoto(this.frameSize, this.mode).subscribe(
      (r) => {
        this.photoWizardState = PhotoWizardState.Preview;
      },
      (error) => this.handleError(error, PhotoWizardState.InitializationError),
    );
    this.setLightMode(CameraSettings.getLightMode(ComponentType.PhotoWizard, selectedCamera));
  }

  private onEnrollComplete() {
    of(null)
      .pipe(
        concatMap((_) => (this.mode === 'hdr' ? this.portraitService.hdr() : of<void>(undefined))),
        concatMap((_) => this.portraitService.download(true)),
        takeUntil(this.dead),
      )
      .subscribe(
        (blob) => {
          this.onCapturedPhotoReceived(blob);
        },
        (error) => {
          this.handleError(error);
        },
      );
  }

  private onCapturedPhotoReceived(blob: Blob): void {
    this.imageSize = this.photoSize;
    this.registerImage(blob, (url) => {
      this.imageSrc = url;
    });
    this.updateImageFitParams();

    setTimeout(() => {
      // this.photoWizardState = PhotoWizardState.EyePositionSearch;
      this.portraitService
        .findEyes()
        .pipe(takeUntil(this.dead))
        .subscribe(
          (eyes) => {
            this.onEyesFound(eyes);
          },
          (error) => {
            this.onEyesNotFound();
          },
        );
    }, 1);
  }

  private onEyesFound(eyes: EyePositionInfo): void {
    this.originalLeftEyePosition = eyes.left;
    this.originalRightEyePosition = eyes.right;
    this.onEyesPositionChanged();
  }

  private onEyesNotFound(): void {
    console.info('Eyes not found');
    let centerX = Math.round(this.imageSize!.width / 2);
    let centerY = Math.round(this.imageSize!.height / 2);
    this.originalLeftEyePosition = new Position(centerX - 90, centerY);
    this.originalRightEyePosition = new Position(centerX + 90, centerY);
    this.onEyesPositionChanged();
  }

  private onEyesPositionChanged(): void {
    this.currentLeftEyePosition = this.originalLeftEyePosition!.copy();
    this.currentRightEyePosition = this.originalRightEyePosition!.copy();
    this.photoWizardState = PhotoWizardState.EyePositionConfirmation;
  }

  private onEyesPositionRegistered(): void {
    this.photoWizardState = PhotoWizardState.PhotoProcessingInProgress;
    of<void>(undefined)
      .pipe(
        concatMap((_) => (this.expasoftOn ? this.portraitService.expasoft() : of<void>(undefined))),
        concatMap((_) => this.portraitService.crop()),
        takeUntil(this.dead),
      )
      .subscribe(
        (result) => {
          this.onPhotoProcessingComplete(result);
        },
        (error) => {
          this.handleError(error, PhotoWizardState.InitializationError);
        },
      );
  }

  private onPhotoProcessingComplete(result: PhotoProcessingResult) {
    this.chipPhoto = null;
    this.portraitPhoto = null;
    Control.readBlob(result.chipPhoto!, (res) => {
      this.chipPhoto = 'data:image/png;base64,' + res;
      this.savePhoto(result);
    });
    Control.readBlob(result.portraitPhoto!, (res) => {
      this.portraitPhoto = 'data:image/png;base64,' + res;
      this.savePhoto(result);
    });
    this.registerImage(result.chipPhoto!, (url) => {
      this.chipPhotoSrc = url;
    });
    this.registerImage(result.portraitPhoto!, (url) => {
      this.portraitPhotoSrc = url;
    });
  }

  private savePhoto(result: PhotoProcessingResult) {
    if (this.chipPhoto && this.portraitPhoto) {
      this.photoWizardState = PhotoWizardState.ShootingResultConfirmation;
      this.photoArray.push(new PhotoWizardResult(this.chipPhoto, this.portraitPhoto, result.quality));
    }
  }

  private registerImage(blob: Blob, callback: (url: SafeUrl) => void) {
    let url = URL.createObjectURL(blob);
    this.wizardContext.registerDataURL(url);
    callback(this.sanitizer.bypassSecurityTrustResourceUrl(url));
  }

  private updateImageFitParams(): void {
    if (!this.containerSize || !this.imageSize) {
      this.imageFitMode = null;
      this.imageResizeRatio = 1;
      this.marginLeft = 0;
      this.marginTop = 0;
      return;
    }
    const containerWidth = this.containerSize.width;
    const containerHeight = this.containerSize.height;
    const containerRatio = containerWidth / containerHeight;
    const imageRatio = this.imageSize.width / this.imageSize.height;
    this.imageFitMode = imageRatio > containerRatio ? 'height' : 'width';
    this.imageResizeRatio =
      this.imageFitMode === 'width' ? this.containerSize.width / this.imageSize.width : this.containerSize.height / this.imageSize.height;
    this.marginLeft =
      this.imageFitMode === 'width' ? 0 : Math.round(0.5 * (this.containerSize.width - this.imageResizeRatio * this.imageSize.width));
    this.marginTop =
      this.imageFitMode === 'height' ? 0 : Math.round(0.5 * (this.containerSize.height - this.imageResizeRatio * this.imageSize.height));
  }

  private calculateElementPosition(position: Position): Position {
    const left = Math.round(position.x * this.imageResizeRatio + this.marginLeft);
    const top = Math.round(position.y * this.imageResizeRatio + this.marginTop);
    return new Position(left, top);
  }

  private handleError(error: any, state: PhotoWizardState | null = null): void {
    console.error(error);
    if (state) {
      this.photoWizardState = state;
    }
  }

  private shutdownServices(): void {
    this.cameraService.lights(false);
    this.cameraService.disconnect();
    this.portraitService.disconnect();
    this.systemService.disconnect().subscribe();

    this.dead.next();
    this.dead.complete();

    this.photoWizardState = PhotoWizardState.Closed;
  }

  private resetParameters(): void {
    this.imageSize = this.frameSize = this.photoSize = this.containerSize = null;
    this.imageSrc = this.chipPhotoSrc = this.portraitPhotoSrc = null;
  }
}

export class PhotoWizardResult {
  public constructor(chipPhoto: string, portraitPhoto: string, quality: number) {
    this.chipPhoto = chipPhoto;
    this.portraitPhoto = portraitPhoto;
    this.quality = quality;
  }
  public chipPhoto: string;
  public portraitPhoto: string;
  public quality: number;
}

export enum PhotoWizardState {
  InitializationInProgress,
  InitializationError,
  Preview,
  RotateCameraScreen,
  ShootingInProgress,
  EyePositionConfirmation,
  PhotoProcessingInProgress,
  ShootingResultConfirmation,
  ShutdownInProgress,
  Closed,
}
